import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { useMemo } from 'react'

import { QUERY_KEYS } from '@api'
import { ConfigurationProperties, ConfigurationPropertiesEnum } from '@types'
import { DeliveryNoteUtils } from '@utils'

import { ConfigurationPropertiesApi } from './api'

const useConfigurationProperties = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const onError = () => {
    notification.error({
      message: t('globals.admin_router.admin_dashboard.settings.get_error'),
    })
  }

  const { data, isLoading } = useQuery(
    [QUERY_KEYS.CONFIGURATION_PROPERTIES],
    ConfigurationPropertiesApi.getConfigurationProperties,
    {
      staleTime: Infinity,
      onError,
    },
  )

  const allocationData =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.ALLOCATION_FREQUENCY,
    )
  const allocationMinutes = allocationData ? parseInt(allocationData.value, 10) : undefined
  const allocationId = allocationData?.id

  const requirementCC =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.REQUESTER_CUSTOMER_CODE,
    )
  const requirementCCId = requirementCC?.id
  const requirementCCValue = requirementCC ? requirementCC.value : undefined

  const deleteTicketsData =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.PRICE_PERCENTAGE,
    )
  const deleteTicketsDays = deleteTicketsData ? parseInt(deleteTicketsData.value, 10) : undefined
  const deleteTicketsId = deleteTicketsData?.id

  const assignmentTicketsData =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.AUTOMATIC_ASSIGNMENT,
    )
  const assignmentTickets = assignmentTicketsData ? String(assignmentTicketsData.value) : 0
  const assignmentTicketsId = assignmentTicketsData?.id

  const preAuthTicketsData =
    data &&
    data.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.SUPPLIER_CAN_CREATE_AUTOMATIC_TICKETS,
    )

  const preAuthTickets = preAuthTicketsData ? String(preAuthTicketsData.value) : 0
  const preAuthTicketsId = preAuthTicketsData?.id
  const cutoffTimeData = data?.find(
    (setting: ConfigurationProperties) => setting.key === ConfigurationPropertiesEnum.DEADLINE_TIME,
  )
  const cutoffTimeId = cutoffTimeData?.id
  const cutoffTimeHour = cutoffTimeData ? parseInt(cutoffTimeData.value, 10) : undefined

  const poBlockEnabledData = data?.find(
    (setting: ConfigurationProperties) =>
      setting.key === ConfigurationPropertiesEnum.PO_BLOCK_ENABLED,
  )
  const poBlockEnabledId = poBlockEnabledData?.id
  const poBlockEnabled = poBlockEnabledData?.value ?? 'false'

  const poBlockMinTonsData = data?.find(
    (setting: ConfigurationProperties) =>
      setting.key === ConfigurationPropertiesEnum.PO_BLOCK_MIN_TONS,
  )
  const poBlockMinTonsId = poBlockMinTonsData?.id
  const poBlockMinTons = poBlockMinTonsData ? parseInt(poBlockMinTonsData.value, 10) : undefined

  const poBlockTonsPerEquipmentData = data?.find(
    (setting: ConfigurationProperties) =>
      setting.key === ConfigurationPropertiesEnum.PO_BLOCK_TONS_PER_EQUIPMENT,
  )
  const poBlockTonsPerEquipmentId = poBlockTonsPerEquipmentData?.id
  const poBlockTonsPerEquipmentMap = useMemo((): Record<string, number> => {
    const base: Record<string, number> = { default: 20 }
    if (!poBlockTonsPerEquipmentData?.value) return base
    try {
      const parsed = JSON.parse(poBlockTonsPerEquipmentData.value) as Record<string, unknown>
      const out = { ...base }
      Object.entries(parsed).forEach(([key, v]) => {
        const n = typeof v === 'number' ? v : parseFloat(String(v))
        if (!Number.isNaN(n)) out[key] = n
      })
      if (out.default === undefined || Number.isNaN(out.default)) out.default = 20
      return out
    } catch {
      return base
    }
  }, [poBlockTonsPerEquipmentData?.value])
  const poBlockTonsPerEquipment = poBlockTonsPerEquipmentMap.default
  const carrierMultiAssignmentData = data?.find(
    (setting: ConfigurationProperties) =>
      setting.key === ConfigurationPropertiesEnum.ALLOW_CARRIER_MULTI_ASSIGNMENT,
  )
  const carrierMultiAssignmentId = carrierMultiAssignmentData?.id
  const carrierMultiAssignmentEnabled = carrierMultiAssignmentData?.value ?? 'false'

  const automaticBillingBatchCloseDaysData = data?.find(
    (setting: ConfigurationProperties) =>
      setting.key === ConfigurationPropertiesEnum.AUTOMATIC_BILLING_BATCH_CLOSE_DAYS,
  )
  const automaticBillingBatchCloseDaysId = automaticBillingBatchCloseDaysData?.id
  const automaticBillingBatchCloseDays = automaticBillingBatchCloseDaysData?.value

  const carrierAvailabilityIsEnabled = useMemo(
    () =>
      data?.find(
        (setting: ConfigurationProperties) =>
          setting.key === ConfigurationPropertiesEnum.USE_CARRIER_AVAILABILITY,
      )?.value === 'true',
    [data],
  )

  const requireTruckAndTrailer = useMemo(
    () =>
      data?.find(
        (setting: ConfigurationProperties) =>
          setting.key === ConfigurationPropertiesEnum.REQUIRE_TRUCK_AND_TRAILER,
      )?.value === 'true',
    [data],
  )

  const requireDriver = useMemo(
    () =>
      data?.find(
        (setting: ConfigurationProperties) =>
          setting.key === ConfigurationPropertiesEnum.REQUIRE_DRIVER,
      )?.value === 'true',
    [data],
  )

  const maxAllowedDaysForSupplierRequest = useMemo(() => {
    const cfg = data?.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.MAX_ALLOWED_DAYS_FOR_SUPPLIER_REQUEST,
    )

    return !!cfg?.value ? parseInt(cfg.value) : 0
  }, [data])

  const minimumDeliveryNoteLength = useMemo(() => {
    const cfg = data?.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.MINIMUM_DELIVERY_NOTE_LENGTH,
    )
    const parsed = cfg?.value ? parseInt(cfg.value, 10) : NaN
    return Number.isNaN(parsed) || parsed <= 0 ? DeliveryNoteUtils.DEFAULT_MIN_LENGTH : parsed
  }, [data])

  const maximumDeliveryNoteLength = useMemo(() => {
    const cfg = data?.find(
      (setting: ConfigurationProperties) =>
        setting.key === ConfigurationPropertiesEnum.MAXIMUM_DELIVERY_NOTE_LENGTH,
    )
    const parsed = cfg?.value ? parseInt(cfg.value, 10) : NaN
    const resolved =
      Number.isNaN(parsed) || parsed <= 0 ? DeliveryNoteUtils.DEFAULT_MAX_LENGTH : parsed
    return Math.max(resolved, minimumDeliveryNoteLength)
  }, [data, minimumDeliveryNoteLength])

  const onMutationSuccessTime = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.successTime'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessPercentaje = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.successPercentaje'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessAssignmentTickets = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.success_assignment_tickets'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessPreauthTickets = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.success_preauth_tickets'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessRequirementCC = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.success_requirement_cc'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessCutoffTime = () => {
    notification.success({
      message: t('The_modification_was_made_successfully'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessAutomaticBillingBatchCloseDays = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.success_automatic_batch_close_days'),
    })

    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessPoBlockEnabled = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.vehicles.success_po_block_enabled'),
    })
    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessCarrierMultiAssignment = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.success_carrier_multi_assignment'),
    })
    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessPoBlockMinTons = () => {
    notification.success({
      message: t('admin_router.admin_dashboard.settings.vehicles.success_po_block_min_tons'),
    })
    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationSuccessPoBlockTonsPerEquipment = () => {
    notification.success({
      message: t(
        'admin_router.admin_dashboard.settings.vehicles.success_po_block_tons_per_equipment',
      ),
    })
    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }
  const onMutationError = (err: AxiosError) => {
    const errorResponse = err.response?.data.error
    notification.error({
      message: t('admin_router.admin_dashboard.settings.error'),
    })
    if (errorResponse.message.toLowerCase().includes('allocation frequency')) {
      notification.error({
        message: t('admin_router.admin_dashboard.settings.error_minutes'),
      })
    }
    queryClient.invalidateQueries(QUERY_KEYS.CONFIGURATION_PROPERTIES)
  }

  const { mutate: updateConfigPropsTime, isLoading: isConfigLoadingTime } = useMutation(
    ConfigurationPropertiesApi.updateConfigurationProperties,
    {
      onSuccess: onMutationSuccessTime,
      onError: onMutationError,
    },
  )
  const { mutate: updateConfigPropsPercentaje, isLoading: isConfigLoadingPercentaje } = useMutation(
    ConfigurationPropertiesApi.updateConfigurationProperties,
    {
      onSuccess: onMutationSuccessPercentaje,
      onError: onMutationError,
    },
  )
  const {
    mutate: updateConfigPropsAssignmentTickets,
    isLoading: isConfigLoadingAssignmentTickets,
  } = useMutation(ConfigurationPropertiesApi.updateConfigurationProperties, {
    onSuccess: onMutationSuccessAssignmentTickets,
    onError: onMutationError,
  })
  const { mutate: updatePreAuthTickets, isLoading: isConfigLoadingUpdatePreAuthTickets } =
    useMutation(ConfigurationPropertiesApi.updateConfigurationProperties, {
      onSuccess: onMutationSuccessPreauthTickets,
      onError: onMutationError,
    })
  const { mutate: updateRequirementCC, isLoading: isConfigLoadingRequirementCC } = useMutation(
    ConfigurationPropertiesApi.updateConfigurationProperties,
    {
      onSuccess: onMutationSuccessRequirementCC,
      onError: onMutationError,
    },
  )
  const { mutate: updateCutoffTime, isLoading: isConfigLoadingCutoffTime } = useMutation(
    ConfigurationPropertiesApi.updateConfigurationProperties,
    {
      onSuccess: onMutationSuccessCutoffTime,
      onError: onMutationError,
    },
  )
  const { mutate: updatePoBlockEnabled, isLoading: isLoadingPoBlockEnabled } = useMutation(
    ConfigurationPropertiesApi.updateConfigurationProperties,
    {
      onSuccess: onMutationSuccessPoBlockEnabled,
      onError: onMutationError,
    },
  )
  const { mutate: updateCarrierMultiAssignment, isLoading: isConfigLoadingCarrierMultiAssignment } =
    useMutation(ConfigurationPropertiesApi.updateConfigurationProperties, {
      onSuccess: onMutationSuccessCarrierMultiAssignment,
      onError: onMutationError,
    })
  const { mutate: updatePoBlockMinTons, isLoading: isLoadingPoBlockMinTons } = useMutation(
    ConfigurationPropertiesApi.updateConfigurationProperties,
    {
      onSuccess: onMutationSuccessPoBlockMinTons,
      onError: onMutationError,
    },
  )
  const { mutate: updatePoBlockTonsPerEquipment, isLoading: isLoadingPoBlockTonsPerEquipment } =
    useMutation(ConfigurationPropertiesApi.updateConfigurationProperties, {
      onSuccess: onMutationSuccessPoBlockTonsPerEquipment,
      onError: onMutationError,
    })
  const {
    mutate: updateAutomaticBillingBatchCloseDays,
    isLoading: isConfigLoadingAutomaticBillingBatchCloseDays,
  } = useMutation(ConfigurationPropertiesApi.updateConfigurationProperties, {
    onSuccess: onMutationSuccessAutomaticBillingBatchCloseDays,
    onError: onMutationError,
  })

  return {
    settings: data,
    isLoading,
    allocationMinutes,
    allocationId,
    deleteTicketsDays,
    deleteTicketsId,
    assignmentTicketsId,
    assignmentTickets,
    preAuthTicketsId,
    preAuthTickets,
    carrierAvailabilityIsEnabled,
    requireTruckAndTrailer,
    assignmentTicketsData,
    updateConfigPropsAssignmentTickets,
    updateConfigPropsTime,
    updateConfigPropsPercentaje,
    isConfigLoadingAssignmentTickets,
    isConfigLoadingTime,
    isConfigLoadingPercentaje,
    updatePreAuthTickets,
    isConfigLoadingUpdatePreAuthTickets,
    maxAllowedDaysForSupplierRequest,
    minimumDeliveryNoteLength,
    maximumDeliveryNoteLength,
    requireDriver,
    requirementCCId,
    requirementCCValue,
    updateRequirementCC,
    isConfigLoadingRequirementCC,
    cutoffTimeId,
    cutoffTimeHour,
    updateCutoffTime,
    isConfigLoadingCutoffTime,
    poBlockEnabled,
    poBlockEnabledId,
    poBlockMinTons,
    poBlockMinTonsId,
    poBlockTonsPerEquipment,
    poBlockTonsPerEquipmentMap,
    poBlockTonsPerEquipmentId,
    updatePoBlockEnabled,
    updatePoBlockMinTons,
    updatePoBlockTonsPerEquipment,
    isLoadingPoBlockEnabled,
    isLoadingPoBlockMinTons,
    isLoadingPoBlockTonsPerEquipment,
    automaticBillingBatchCloseDaysId,
    automaticBillingBatchCloseDays,
    updateAutomaticBillingBatchCloseDays,
    isConfigLoadingAutomaticBillingBatchCloseDays,
    carrierMultiAssignmentId,
    carrierMultiAssignmentEnabled,
    updateCarrierMultiAssignment,
    isConfigLoadingCarrierMultiAssignment,
  }
}

export { useConfigurationProperties }
